import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Autocomplete } from "@mui/material";
import { useAppSelector } from "../../stores/store";
import "./index.css";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CollectionSite, TrapType, CollectionsFormData } from "../../types";
import type { Dayjs } from "dayjs";

interface Props {
  interval?: number;
  handleSubmit: (formData: CollectionsFormData, init?: boolean) => void;
  handleDownload?: () => void;
}

const BatchDataFilter: React.FC<Props> = ({
  interval = 500,
  handleSubmit,
  handleDownload,
}) => {
  const devices = useAppSelector((state) => state?.devices?.devices);
  const devicesLoading = useAppSelector((state) => state?.devices?.loading);
  const collectionSitesOptions = useAppSelector(
    (state) => state.collectionSites.collectionSites
  );
  const collectionSitesOptionsLoading = useAppSelector(
    (state) => state.collectionSites.collectionSitesLoading
  );
  const trapTypesOptions = useAppSelector(
    (state) => state.collectionSites.trapTypes
  );
  const trapTypesOptionsLoading = useAppSelector(
    (state) => state.collectionSites.trapTypesLoading
  );
  const [formData, setFormData] = useState<CollectionsFormData>({
    selectedDevice: [],
    startingDate: null,
    endingDate: null,
    trapTypes: [],
    collectionSites: [],
  });

  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    handleSubmit(formData, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange =
    (name: keyof CollectionsFormData) => (newValue: Dayjs | null) => {
      setFormData({
        ...formData,
        [name]: newValue,
      });
      debounceSubmit();
    };

  const handleAutocompleteChange =
    (name: keyof CollectionsFormData) =>
    (
      _: React.SyntheticEvent,
      value: TrapType[] | CollectionSite[] | string | string[]
    ) => {
      setFormData({
        ...formData,
        [name]: value,
      });
      debounceSubmit();
    };

  const debounceSubmit = () => {
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(
      setTimeout(() => {
        handleSubmit(formData);
      }, interval)
    );
  };

  return (
    <Box component="form" className="box-batch-data-filter">
      <Autocomplete
        disablePortal
        options={devices}
        sx={{ width: "300px" }}
        value={formData.selectedDevice}
        getOptionLabel={(cur) => cur?.displayName || cur?.id || ""}
        getOptionKey={(cur) => cur.id || ""}
        multiple
        renderInput={(params) => <TextField {...params} label="Devices" />}
        onChange={handleAutocompleteChange("selectedDevice")}
        loading={devicesLoading}
      />
      <DatePicker
        label="From Date:"
        value={formData.startingDate}
        slotProps={{
          field: { clearable: true, onClear: () => {} },
        }}
        onChange={handleDateChange("startingDate")}
      />
      <DatePicker
        label="To Date:"
        slotProps={{
          field: { clearable: true, onClear: () => {} },
        }}
        value={formData.endingDate}
        onChange={handleDateChange("endingDate")}
      />
      <Autocomplete
        disablePortal
        options={trapTypesOptions}
        sx={{ width: 300 }}
        getOptionLabel={(cur) => cur.displayName || ""}
        getOptionKey={(cur) => cur.id || ""}
        value={formData.trapTypes}
        multiple
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => <TextField {...params} label="TrapType" />}
        onChange={handleAutocompleteChange("trapTypes")}
        loading={trapTypesOptionsLoading}
      />

      <Autocomplete
        disablePortal
        options={collectionSitesOptions}
        sx={{ width: 300 }}
        getOptionLabel={(cur) => cur.displayName || ""}
        value={formData.collectionSites}
        multiple
        renderInput={(params) => (
          <TextField {...params} label="Collection Sites" />
        )}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        onChange={handleAutocompleteChange("collectionSites")}
        loading={collectionSitesOptionsLoading}
      />
      <Box>
        <Button
          variant="contained"
          color="primary"
          sx={{ width: "100px" }}
          onClick={() => debounceSubmit()}
        >
          view
        </Button>
      </Box>
      {handleDownload && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            sx={{ width: "100px" }}
            onClick={() => handleDownload()}
          >
            Download
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BatchDataFilter;
